import { useShouldRemoveFileUploadPrefix } from '@modules/common/hooks';
import { getQuantifyAggregateFormulaResponse } from '@modules/quantify/hooks/useFetchQuantifyTableData/helpers/getQuantifyAggregateFormulaResponse';
import { getQuantifyTableRows } from '@modules/quantify/hooks/useFetchQuantifyTableData/helpers/getQuantifyTableRows';
import { useExecuteQuantifyTableEql } from '@modules/quantify/hooks/useFetchQuantifyTableData/useExecuteQuantifyTableEql';
import { useUpdateQuantifyStoreIfRequired } from '@modules/quantify/hooks/useFetchQuantifyTableData/useUpdateQuantifyStoreIfRequired';
import { useIsSingleRecordQuery } from '@modules/quantify/hooks/useIsSingleRecordQuery';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { isValidQuery } from '@modules/quantify/utils';
import { getCompareDataPointsFromAggregatesResponse } from '@modules/quantify/utils/getCompareDataPointsFromAggregatesResponse';
import { useCallback, useRef } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { shallow } from 'zustand/shallow';

interface useFetchQuantifyTableDataParams {
  isInitialFetch?: boolean;
  shouldFetchCount?: boolean;
}

export function useFetchQuantifyTableData(
  options: useFetchQuantifyTableDataParams = {},
) {
  const { isInitialFetch } = options;

  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const updateQuantifyStoreIfRequired = useUpdateQuantifyStoreIfRequired({
    isInitialFetch,
  });
  const [lazyQuery, { status, error }] = useExecuteQuantifyTableEql({
    shouldFetchCount: options.shouldFetchCount,
  });
  const isSingleRecordQuery = useIsSingleRecordQuery({
    formulaID: options.shouldFetchCount ? EQLFunctionName.Count : undefined,
  });
  const quantifyState = useQuantifyStore(({ query, metadata }) => {
    return {
      visibleDataPoints: metadata.visibleDataPoints,
      shouldShowAllValues: metadata.shouldShowAllValues,
      shouldShowTopDataPoints: metadata.shouldShowTopDataPoints,
      groupbyDisplayName: query.groupBy?.displayName,
      measuredAsCriteria: query.measuredAsCriteria,
      query: query,
    };
  }, shallow);
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();

  const dependencies = {
    ...quantifyState,
    isSingleRecordQuery,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const fetchQuantifyTableData = useCallback(async () => {
    const isMeasuredAsCriteriaRequiredButNotPresent =
      !dependenciesRef.current.measuredAsCriteria;

    if (
      !isValidQuery(dependenciesRef.current.query) ||
      isMeasuredAsCriteriaRequiredButNotPresent
    ) {
      dispatch({
        type: QuantifyActionType.SET_REPORT_DATA,
        payload: {
          quantify: { tableRows: null, aggregateFormulaResponse: null },
          compare: { tableRows: null, aggregateFormulaResponse: null },
        },
      });
      return;
    }

    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: { isTableQueryInProgress: true },
    });

    try {
      const resp = await lazyQuery();
      const result = resp?.data?.executeEQL?.result;

      if (result?.__typename === 'GetAggregatesResponse' && result.dataV2) {
        if (dependenciesRef.current.isSingleRecordQuery) {
          const aggregateFormulaResponse = getQuantifyAggregateFormulaResponse({
            aggregateDataV2List: result.dataV2,
          });

          dispatch({
            type: QuantifyActionType.SET_REPORT_DATA,
            payload: {
              quantify: {
                aggregateFormulaResponse: aggregateFormulaResponse
                  ? {
                      ...aggregateFormulaResponse,
                      label: dependenciesRef.current.groupbyDisplayName ?? '',
                    }
                  : aggregateFormulaResponse,
              },
            },
          });

          return;
        }

        const records = getQuantifyTableRows({
          shouldRemoveFileUploadPrefix,
          aggregateDataV2List: result.dataV2,
          whitelistIDs:
            dependenciesRef.current.shouldShowAllValues ||
            dependenciesRef.current.shouldShowTopDataPoints
              ? undefined
              : dependenciesRef.current.visibleDataPoints,
        });

        updateQuantifyStoreIfRequired({
          quantifyTableRows: records,
          aggregateHits: result.hits ?? 0,
        });
      }

      if (result?.__typename === 'GetDeltaAggregatesResponse') {
        if (dependenciesRef.current.isSingleRecordQuery) {
          const { records: aggregateFormulaResponse } =
            getCompareDataPointsFromAggregatesResponse({
              deltaAggregatesResponseView: result,
              shouldRemoveFileUploadPrefix,
            });

          dispatch({
            type: QuantifyActionType.SET_REPORT_DATA,
            payload: {
              compare: {
                aggregateFormulaResponse: !!aggregateFormulaResponse[0]
                  ? {
                      ...aggregateFormulaResponse[0],
                      label: dependenciesRef.current.groupbyDisplayName ?? '',
                    }
                  : undefined,
              },
            },
          });

          return;
        }

        const { records, aggregateHits } =
          getCompareDataPointsFromAggregatesResponse({
            shouldRemoveFileUploadPrefix,
            deltaAggregatesResponseView: result,
            whitelistIDs:
              dependenciesRef.current.shouldShowAllValues ||
              dependenciesRef.current.shouldShowTopDataPoints
                ? undefined
                : dependenciesRef.current.visibleDataPoints,
          });

        updateQuantifyStoreIfRequired({
          compareTableRows: records,
          aggregateHits: aggregateHits ?? 0,
        });
      }
    } finally {
      dispatch({
        type: QuantifyActionType.SET_VISUAL,
        payload: { isTableQueryInProgress: false },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyQuery, dispatch]);

  return [
    fetchQuantifyTableData,
    { isLoading: IsLoading(status), error },
  ] as const;
}
